import { CommonModule } from '@angular/common';
import { Component, computed, model, OnInit, Signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslationModule } from '../../../../i18n/translation-module';
import { CrmFieldService, StandardIds } from '../../../../shared-services/crm-services/field.service';
import { ActivityInterface } from '@vendasta/crm';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';

import { ActivityRichTextComponent } from '../../activities/activity-rich-text.component';
import { ActivityAssociationService, ActivityIcons, ActivityTypes, CrmObjectIdentifier } from '../../activities';
import { User } from '@vendasta/iamv2';
import { ActivityCardComponent } from './system-activities/activity-card/activity-card.component';
import { RouterLink } from '@angular/router';
import { CommunicationSubType } from '../../../../tokens-and-interfaces';

@Component({
  selector: 'crm-activity-logged-communication-v2',
  templateUrl: './logged-communication-v2.component.html',
  styleUrls: ['./logged-communication-v2.component.scss'],
  providers: [],
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    GalaxyPipesModule,
    ActivityRichTextComponent,
    ActivityCardComponent,
    RouterLink,
  ],
})
export class CrmActivityLoggedCommunicationV2Component implements OnInit {
  private unknownNamePlaceholder = this.translate.instant('CONTACT.ACTIVITIES.UNKNOWN_NAME');
  activity = model<ActivityInterface>({});
  ownerUser = model<User | undefined>(undefined);
  associatedObject = model<CrmObjectIdentifier | undefined>(undefined);

  icon = computed(() => ActivityIcons[ActivityTypes.Communication]);

  fullName = computed(() => {
    const user = this.ownerUser() as User;
    return `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || this.unknownNamePlaceholder;
  });

  showPrimaryAssociation = computed(() => !this.associatedObject()?.crmObjectId);
  primaryAssociation = this.associationService.primaryAssociation;
  routePrefix = this.associationService.routePrefix;
  primaryAssociationLink = computed(() => {
    if (!this.primaryAssociation()) return '';
    const linkUrl = this.primaryAssociation()?.linkUrl;
    return `${this.routePrefix()}/${linkUrl}`;
  });

  cardInfo: Signal<ActivityCardInformation> = computed(() => {
    return {
      title: this.getTitleByChannel(),
      body: this.getStringFieldData(this.activity(), StandardIds.ActivityCommunicationBody),
      activityUrl: this.getStringFieldData(this.activity(), StandardIds.ActivityUrl),
      activityUrlName: this.getStringFieldData(this.activity(), StandardIds.ActivityUrlName),
      outcome: this.getStringFieldData(this.activity(), StandardIds.ActivityCommunicationOutcome),
    };
  });

  private getStringFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }

  private getTitleByChannel() {
    const baseKey = 'ACTIVITY.LOGGED_ACTIVITY.HEADER_ADDITIONAL_TEXT.COMMUNICATION';
    const communicationChannel = this.getStringFieldData(
      this.activity(),
      StandardIds.ActivityCommunicationChannel,
    ) as CommunicationSubType;
    const typeWithVerb = `${baseKey}.${communicationChannel.toUpperCase()}`;
    const preposition = 'ACTIVITY.LOGGED_ACTIVITY.HEADER_ADDITIONAL_TEXT.BY';
    return {
      typeWithVerb,
      preposition,
    };
  }

  constructor(
    private readonly fieldService: CrmFieldService,
    private readonly associationService: ActivityAssociationService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.associationService.activity = this.activity();
  }
}

interface ActivityHeaderParts {
  typeWithVerb: string;
  preposition: string;
}

interface ActivityCardInformation {
  title: ActivityHeaderParts;
  body: string;
  activityUrl?: string;
  activityUrlName: string;
  outcome: string;
}
